import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchOptionChain } from '../slices/optionChainSlice';
import { Container, Row, Col, Card, Table, Spinner, Alert, Badge, Form } from 'react-bootstrap';

const OptionChain = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const optionChainState = useSelector((state) => state.optionChainSlice);

  const [symbol, setSymbol] = useState('NSE_INDEX|Nifty 50');
  const [expiry, setExpiry] = useState('2025-06-05');
  const [expiryOptions, setExpiryOptions] = useState([]);

  const expiryDates = useMemo(() => {
    // Get current IST date
    const today = new Date();
    const istToday = new Date(today.getTime() + (5.5 * 60 * 60 * 1000));
    
    // Generate upcoming Thursdays dynamically
    const getUpcomingThursdays = (count = 5) => {
      const dates = [];
      let current = new Date(istToday);
      
      // Include today if it's Thursday
      if (istToday.getDay() === 4) {
        const nextDay = new Date(istToday);
        nextDay.setDate(istToday.getDate() + 7);
        const isLastThursday = (nextDay.getMonth() !== istToday.getMonth());
        const dateStr = istToday.toISOString().split('T')[0];
        dates.push(dateStr);
      }
      
      // Get future Thursdays
      while (dates.length < count) {
        current.setDate(current.getDate() + 1);
        if (current.getDay() === 4) {
          const dateStr = current.toISOString().split('T')[0];
          dates.push(dateStr);
        }
      }
      return dates;
    };
    
    return {
      'NSE_INDEX|Nifty 50': getUpcomingThursdays(),
      'NSE_INDEX|Nifty Bank': getUpcomingThursdays(),
    };
  }, []);

  useEffect(() => {
    // Update expiry options based on selected symbol
    setExpiryOptions(expiryDates[symbol] || []);
    setExpiry(expiryDates[symbol][0] || ''); // Set default expiry to the first option
  }, [symbol, expiryDates]);

  useEffect(() => {
    console.log('Dispatching fetchOptionChain action');
    dispatch(fetchOptionChain({ symbol, expiry }));
  }, [dispatch, symbol, expiry]);

  useEffect(() => {
    console.log('Option chain state:', optionChainState);
  }, [optionChainState]);

  if (!optionChainState) {
    return <Alert variant="info">Loading...</Alert>;
  }

  const { data, loading, error } = optionChainState;

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return <Alert variant="danger">Error: {error}</Alert>;
  }

  if (!data || !data.data || !Array.isArray(data.data) || data.data.length === 0) {
    return <Alert variant="info">No data available</Alert>;
  }

  const optionChainData = data.data;
  const generalInfo = optionChainData[0] || {};

  // const handleNavigate = (path, param) => {
  //   navigate(`${path}/${param}`);
  // };

  const renderOptionRow = (option) => {
    const { strike_price, call_options, put_options } = option;
    const isLesser = strike_price < generalInfo.underlying_spot_price ? { backgroundColor: '#f7efd5' } : {};
    const isGreater = strike_price > generalInfo.underlying_spot_price ? { backgroundColor: '#f7efd5' } : {};
    

    return (
      <tr key={strike_price} >
        <td style={{ ...isLesser, textAlign: 'center' }}>{renderOptionData(call_options.market_data.oi)}</td>
        <td style={{...isLesser, textAlign: 'center'}}>{renderOptionData(call_options.market_data.volume)}</td>
        <td 
          className="clickable" 
          onClick={() => window.open(`/chart/upstox/${call_options.instrument_key}`, '_blank')}
          style={{ cursor: 'pointer', color: 'blue', textDecoration: 'underline' ,...isLesser, textAlign: 'center'}}
        >
          {renderOptionData(call_options.market_data.ltp)}
        </td>
        <td 
          className="strike-price" 
          style={{ cursor: 'pointer', textAlign: 'center' }}
        >
          {strike_price}
        </td>
        <td 
          className="clickable" 
          onClick={() => window.open(`/chart/upstox/${put_options.instrument_key}`, '_blank')}
          style={{ cursor: 'pointer', color: 'blue', textDecoration: 'underline', ...isGreater, textAlign: 'center' }}
        >
          {renderOptionData(put_options.market_data.ltp)}
        </td>
        <td style={{...isGreater, textAlign: 'center'}}>{renderOptionData(put_options.market_data.volume)}</td>
        <td style={{...isGreater, textAlign: 'center'}}>{renderOptionData(put_options.market_data.oi)}</td>
      </tr>
    );
  };

  const renderOptionData = (value) => {
    return value !== undefined ? value.toFixed(2) : '-';
  };

  const spotPrice = generalInfo.underlying_spot_price;
  const lesserThanSpot = [];
  const greaterThanSpot = [];

  // Find the closest strike prices
  optionChainData.forEach(option => {
    if (option.strike_price < spotPrice) {
      lesserThanSpot.push(option);
    } else if (option.strike_price > spotPrice) {
      greaterThanSpot.push(option);
    }
  });

  // Sort the arrays to find the nearest strike prices
  lesserThanSpot.sort((a, b) => b.strike_price - a.strike_price); // Sort descending for lesser
  greaterThanSpot.sort((a, b) => a.strike_price - b.strike_price); // Sort ascending for greater

  // Take the closest 15 lesser and 15 greater strike prices
  const filteredOptions = [
    ...lesserThanSpot.slice(0, 15),
    ...greaterThanSpot.slice(0, 15)
  ];

  // Sort the combined filtered options in ascending order by strike_price
  filteredOptions.sort((a, b) => a.strike_price - b.strike_price);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === 'symbol') {
      setSymbol(value); // Update symbol state
    } else if (name === 'expiry') {
      setExpiry(value); // Update expiry state
    }
  };

  return (
    <Container fluid className="p-0">
      <Container>
        <Card className="mb-3">
          <Card.Body>
            {optionChainState.error && (
              <div className="error-message">
                {/* Render error message safely */}
                {typeof optionChainState.error === 'string' 
                  ? optionChainState.error 
                  : optionChainState.error.message || 'An error occurred'}
              </div>
            )}
            <Form>
              <Row>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Symbol</Form.Label>
                    <Form.Select
                      name="symbol"
                      value={symbol}
                      onChange={handleInputChange}
                    >
                      <option value="NSE_INDEX|Nifty 50">NIFTY</option>
                      <option value="NSE_INDEX|Nifty Bank">BANKNIFTY</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Spot Price</Form.Label>
                    <Form.Control
                      name="spot_price"
                      value={generalInfo.underlying_spot_price || 'N/A'}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Expiry</Form.Label>
                    <Form.Select
                      name="expiry"
                      value={expiry}
                      onChange={handleInputChange}
                    >
                      {expiryOptions.map((date) => (
                        <option key={date} value={date}>{date}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>

        <Row>
          <Col>
            <div className="table-responsive">
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th colSpan="3" >CALL</th>
                    <th rowSpan="2">Strike Price</th>
                    <th colSpan="3" >PUT</th>
                  </tr>
                  <tr>
                    <th >OI</th>
                    <th>Volume</th>
                    <th>LTP</th>
                    <th>LTP</th>
                    <th>Volume</th>
                    <th>OI</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOptions.map(renderOptionRow)}
                </tbody>
              </Table>
            </div>
          </Col>
        </Row>
      </Container>
    </Container>
  );
};

export default OptionChain;
